/**
 * Webtech HR Engine™ - AI Credits Manager
 * Interface SuperAdmin pour gérer les crédits IA des entreprises
 */

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';

// UI Components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Alert,
  AlertDescription,
} from './ui/alert';

// Icons
import {
  Sparkles,
  Plus,
  Minus,
  Edit,
  Save,
  Loader2,
  Building2,
  History,
  Coins,
  Settings,
  RefreshCw,
  Search,
  Gift,
  Zap,
  TrendingDown,
  TrendingUp,
  AlertCircle,
  ChevronRight,
} from 'lucide-react';

// Format number with spaces
const formatNumber = (num) => {
  return new Intl.NumberFormat('fr-FR').format(num || 0);
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// AI Costs Configuration Component
function AICostsConfig({ onUpdate }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({ costs: [], welcomeCredits: 100 });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ai/superadmin/credits/config');
      setConfig({
        costs: response.data.costs || [],
        welcomeCredits: response.data.welcomeCredits || 100
      });
    } catch (error) {
      console.error('Error fetching AI config:', error);
      toast.error('Erreur lors du chargement de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleCostChange = (index, field, value) => {
    setConfig(prev => {
      const newCosts = [...prev.costs];
      newCosts[index] = { ...newCosts[index], [field]: value };
      return { ...prev, costs: newCosts };
    });
    setHasChanges(true);
  };

  const handleWelcomeCreditsChange = (value) => {
    setConfig(prev => ({ ...prev, welcomeCredits: parseInt(value) || 0 }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Update welcome credits
      await api.put('/ai/superadmin/credits/welcome', null, {
        params: { welcome_credits: config.welcomeCredits }
      });
      
      // Update each cost
      for (const cost of config.costs) {
        await api.put(`/ai/superadmin/credits/cost/${cost.actionId}`, {
          creditCost: cost.creditCost,
          isActive: cost.isActive
        });
      }
      
      toast.success('Configuration mise à jour');
      setHasChanges(false);
      onUpdate?.();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Settings className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Configuration des Coûts IA</CardTitle>
              <CardDescription>Définissez le coût en crédits de chaque action IA</CardDescription>
            </div>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving || !hasChanges}
            className="bg-webtech-blue hover:bg-webtech-blue-hover"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Enregistrer
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Welcome Credits */}
        <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Gift className="w-6 h-6 text-purple-600" />
              <div>
                <p className="font-medium text-purple-900">Crédits de bienvenue</p>
                <p className="text-sm text-purple-700">Crédits offerts aux nouvelles entreprises</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={config.welcomeCredits}
                onChange={(e) => handleWelcomeCreditsChange(e.target.value)}
                className="w-24 text-center font-bold"
                min={0}
              />
              <span className="text-purple-700 font-medium">crédits</span>
            </div>
          </div>
        </div>

        <Separator />

        {/* Costs Table */}
        <div className="space-y-4">
          <h4 className="font-medium text-slate-900 flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" />
            Coûts des actions IA
          </h4>
          
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Action</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-center">Coût (crédits)</TableHead>
                <TableHead className="text-center">Actif</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {config.costs.map((cost, index) => (
                <TableRow key={cost.actionId}>
                  <TableCell className="font-medium">{cost.actionName}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{cost.actionDescription}</TableCell>
                  <TableCell className="text-center">
                    <Input
                      type="number"
                      value={cost.creditCost}
                      onChange={(e) => handleCostChange(index, 'creditCost', parseInt(e.target.value) || 0)}
                      className="w-20 text-center mx-auto"
                      min={1}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <input
                      type="checkbox"
                      checked={cost.isActive}
                      onChange={(e) => handleCostChange(index, 'isActive', e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

// Companies Credits List Component
function CompaniesCredits({ onSelectCompany }) {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/ai/superadmin/companies/credits');
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Error fetching companies credits:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const filteredCompanies = companies.filter(c => 
    c.name?.toLowerCase().includes(search.toLowerCase())
  );

  // Stats
  const totalCredits = companies.reduce((sum, c) => sum + (c.aiCredits || 0), 0);
  const lowBalanceCount = companies.filter(c => (c.aiCredits || 0) < 10).length;

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Crédits par Entreprise</CardTitle>
              <CardDescription>{companies.length} entreprises • {formatNumber(totalCredits)} crédits totaux</CardDescription>
            </div>
          </div>
          <Button variant="outline" onClick={fetchCompanies}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="p-3 bg-blue-50 rounded-lg text-center">
            <p className="text-2xl font-bold text-blue-700">{companies.length}</p>
            <p className="text-xs text-blue-600">Entreprises</p>
          </div>
          <div className="p-3 bg-purple-50 rounded-lg text-center">
            <p className="text-2xl font-bold text-purple-700">{formatNumber(totalCredits)}</p>
            <p className="text-xs text-purple-600">Crédits totaux</p>
          </div>
          <div className="p-3 bg-amber-50 rounded-lg text-center">
            <p className="text-2xl font-bold text-amber-700">{lowBalanceCount}</p>
            <p className="text-xs text-amber-600">Soldes faibles (&lt;10)</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une entreprise..."
            className="pl-10"
          />
        </div>

        {/* Companies List */}
        <div className="max-h-[400px] overflow-y-auto space-y-2">
          {filteredCompanies.map((company) => (
            <div
              key={company.id}
              className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
              onClick={() => onSelectCompany(company)}
            >
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">{company.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge 
                  className={`${
                    company.aiCredits >= 50 
                      ? 'bg-green-100 text-green-700' 
                      : company.aiCredits >= 10 
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-red-100 text-red-700'
                  }`}
                >
                  <Coins className="w-3 h-3 mr-1" />
                  {formatNumber(company.aiCredits)} crédits
                </Badge>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            </div>
          ))}
          
          {filteredCompanies.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <Building2 className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>Aucune entreprise trouvée</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Add/Remove Credits Dialog
function AddCreditsDialog({ company, open, onClose, onSuccess }) {
  const [amount, setAmount] = useState(100);
  const [reason, setReason] = useState('Ajout manuel par SuperAdmin');
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(true);

  const handleSubmit = async () => {
    if (amount <= 0) {
      toast.error('Le montant doit être supérieur à 0');
      return;
    }
    
    setLoading(true);
    try {
      const finalAmount = isAdding ? amount : -amount;
      await api.post(`/ai/superadmin/companies/${company.id}/credits`, {
        amount: finalAmount,
        reason: reason || (isAdding ? 'Ajout de crédits' : 'Retrait de crédits')
      });
      
      toast.success(
        isAdding 
          ? `${amount} crédits ajoutés à ${company.name}`
          : `${amount} crédits retirés de ${company.name}`
      );
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-purple-600" />
            Gérer les crédits - {company?.name}
          </DialogTitle>
          <DialogDescription>
            Solde actuel: <span className="font-bold">{formatNumber(company?.aiCredits || 0)}</span> crédits
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Add/Remove Toggle */}
          <div className="flex gap-2">
            <Button
              variant={isAdding ? 'default' : 'outline'}
              className={isAdding ? 'bg-green-600 hover:bg-green-700' : ''}
              onClick={() => setIsAdding(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
            <Button
              variant={!isAdding ? 'default' : 'outline'}
              className={!isAdding ? 'bg-red-600 hover:bg-red-700' : ''}
              onClick={() => setIsAdding(false)}
            >
              <Minus className="w-4 h-4 mr-2" />
              Retirer
            </Button>
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label>Montant (crédits)</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
              min={1}
              placeholder="100"
            />
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label>Raison</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Raison de l'ajustement..."
            />
          </div>

          {/* Preview */}
          <Alert className={isAdding ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            <AlertDescription className="flex items-center gap-2">
              {isAdding ? (
                <TrendingUp className="w-4 h-4 text-green-600" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-600" />
              )}
              <span>
                Nouveau solde: <strong>{formatNumber((company?.aiCredits || 0) + (isAdding ? amount : -amount))}</strong> crédits
              </span>
            </AlertDescription>
          </Alert>

          {!isAdding && amount > (company?.aiCredits || 0) && (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-amber-700">
                Attention: le retrait dépasse le solde actuel
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || amount <= 0}
            className={isAdding ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isAdding ? 'Ajouter' : 'Retirer'} {amount} crédits
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Company Credits History Dialog
function CreditsHistoryDialog({ company, open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (open && company) {
      fetchHistory();
    }
  }, [open, company]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/ai/superadmin/companies/${company.id}/credits/history`);
      setHistory(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-blue-600" />
            Historique - {company?.name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <History className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>Aucune transaction</p>
            </div>
          ) : (
            <div className="space-y-2">
              {history.map((tx) => (
                <div 
                  key={tx.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    tx.amount > 0 ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {tx.amount > 0 ? (
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-red-600" />
                    )}
                    <div>
                      <p className="font-medium text-slate-900">{tx.description}</p>
                      <p className="text-xs text-slate-500">{formatDate(tx.createdAt)}</p>
                    </div>
                  </div>
                  <Badge className={tx.amount > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                    {tx.amount > 0 ? '+' : ''}{formatNumber(tx.amount)}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main AICreditsManager Component
export default function AICreditsManager() {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showAddCredits, setShowAddCredits] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleSelectCompany = (company) => {
    setSelectedCompany(company);
    setShowAddCredits(true);
  };

  const handleViewHistory = (company) => {
    setSelectedCompany(company);
    setShowHistory(true);
  };

  const handleSuccess = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">Gestion des Crédits IA</h2>
          <p className="text-slate-600">Configurez les coûts et gérez les crédits des entreprises</p>
        </div>
      </div>

      {/* AI Costs Configuration */}
      <AICostsConfig onUpdate={handleSuccess} />

      {/* Companies Credits */}
      <CompaniesCredits 
        key={refreshKey}
        onSelectCompany={handleSelectCompany}
      />

      {/* Add Credits Dialog */}
      <AddCreditsDialog
        company={selectedCompany}
        open={showAddCredits}
        onClose={() => {
          setShowAddCredits(false);
          setSelectedCompany(null);
        }}
        onSuccess={handleSuccess}
      />

      {/* History Dialog */}
      <CreditsHistoryDialog
        company={selectedCompany}
        open={showHistory}
        onClose={() => {
          setShowHistory(false);
          setSelectedCompany(null);
        }}
      />
    </div>
  );
}
